import {
  CognitoIdentityProviderClient,
  SignUpCommand,
  ConfirmSignUpCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  GetUserCommand,
  ChangePasswordCommand,
  CognitoIdentityProviderServiceException
} from '@aws-sdk/client-cognito-identity-provider';
import type {
  SignUpCommandInput,
  InitiateAuthCommandInput
} from '@aws-sdk/client-cognito-identity-provider';
import type { CognitoUser } from '@/types';
import { logger, ValidationError, NetworkError } from '@/utils/logger';

export class CognitoAuthService {
  private client: CognitoIdentityProviderClient;
  // User pool ID kept for potential future use
  // private readonly _userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || 'us-east-2_PLACEHOLDER';
  private readonly clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || 'PLACEHOLDER_CLIENT_ID';
  private readonly region = import.meta.env.VITE_AWS_REGION || 'us-east-2';

  constructor() {
    // For client-side Cognito operations (SignUp, SignIn, etc.),
    // no AWS credentials are needed - the SDK uses unauthenticated API access
    this.client = new CognitoIdentityProviderClient({
      region: this.region
    });
  }

  async signUp(email: string, password: string, name: string, companyName: string): Promise<{ userId: string; confirmationRequired: boolean }> {
    if (!email || !password || !name || !companyName) {
      throw new ValidationError('Email, password, name, and company name are required');
    }

    if (password.length < 8) {
      throw new ValidationError('Password must be at least 8 characters long');
    }

    if (!email.includes('@')) {
      throw new ValidationError('Please enter a valid email address');
    }

    if (companyName.length < 2) {
      throw new ValidationError('Company name must be at least 2 characters long');
    }

    try {
      const params: SignUpCommandInput = {
        ClientId: this.clientId,
        Username: email,
        Password: password,
        UserAttributes: [
          {
            Name: 'email',
            Value: email
          },
          {
            Name: 'name',
            Value: name
          },
          {
            Name: 'custom:companyName',
            Value: companyName
          }
        ]
      };

      const command = new SignUpCommand(params);
      const response = await this.client.send(command);

      logger.info('User signed up successfully:', { email, userId: response.UserSub });

      return {
        userId: response.UserSub || email, // Fallback to email if UserSub not available
        confirmationRequired: !response.UserConfirmed
      };

    } catch (error: unknown) {
      logger.error('Cognito signup error:', error);
      
      const err = error as Error;
      if (error && typeof error === 'object' && 'name' in error) {
        const cognitoError = error as CognitoIdentityProviderServiceException;
        if (cognitoError.name === 'UsernameExistsException') {
          throw new ValidationError('An account with this email already exists');
        }
        if (cognitoError.name === 'InvalidPasswordException') {
          throw new ValidationError('Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, number, and special character');
        }
        if (cognitoError.name === 'InvalidParameterException') {
          throw new ValidationError('Invalid email format or other parameter error');
        }
      }
      
      throw new NetworkError(`Signup failed: ${err.message}`);
    }
  }

  async confirmSignUp(email: string, confirmationCode: string): Promise<void> {
    if (!email || !confirmationCode) {
      throw new ValidationError('Email and confirmation code are required');
    }

    try {
      const command = new ConfirmSignUpCommand({
        ClientId: this.clientId,
        Username: email,
        ConfirmationCode: confirmationCode
      });

      await this.client.send(command);
      logger.info('User confirmation successful:', { email });

    } catch (error: unknown) {
      logger.error('Cognito confirmation error:', error);
      
      const err = error as Error;
      if (error && typeof error === 'object' && 'name' in error) {
        const cognitoError = error as CognitoIdentityProviderServiceException;
        if (cognitoError.name === 'CodeMismatchException') {
          throw new ValidationError('Invalid confirmation code');
        }
        if (cognitoError.name === 'ExpiredCodeException') {
          throw new ValidationError('Confirmation code has expired');
        }
      }
      
      throw new NetworkError(`Confirmation failed: ${err.message}`);
    }
  }

  async signIn(email: string, password: string): Promise<CognitoUser | { requiresNewPassword: true; session: string; email: string }> {
    if (!email || !password) {
      throw new ValidationError('Email and password are required');
    }

    try {
      const params: InitiateAuthCommandInput = {
        ClientId: this.clientId,
        AuthFlow: 'USER_PASSWORD_AUTH',
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password
        }
      };

      const command = new InitiateAuthCommand(params);
      const response = await this.client.send(command);

      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        // User needs to set a new password
        logger.info('User requires new password:', { email });
        return {
          requiresNewPassword: true,
          session: response.Session!,
          email: email
        };
      }

      if (response.ChallengeName) {
        // Handle other challenges (MFA, etc.)
        throw new NetworkError(`Authentication challenge not supported: ${response.ChallengeName}`);
      }

      if (!response.AuthenticationResult) {
        throw new NetworkError('Authentication failed - no tokens received');
      }

      // Get user details to extract company name
      const getUserCommand = new GetUserCommand({
        AccessToken: response.AuthenticationResult.AccessToken!
      });

      const userResponse = await this.client.send(getUserCommand);

      const name = userResponse.UserAttributes?.find(attr => attr.Name === 'name')?.Value || '';
      const companyName = userResponse.UserAttributes?.find(attr => attr.Name === 'custom:companyName')?.Value || '';

      const cognitoUser: CognitoUser = {
        userId: userResponse.Username || email,
        email: email,
        name: name,
        companyName: companyName,
        accessToken: response.AuthenticationResult.AccessToken!,
        idToken: response.AuthenticationResult.IdToken!,
        refreshToken: response.AuthenticationResult.RefreshToken!
      };

      // Store tokens in localStorage for persistence
      localStorage.setItem('cognito_tokens', JSON.stringify({
        accessToken: cognitoUser.accessToken,
        idToken: cognitoUser.idToken,
        refreshToken: cognitoUser.refreshToken
      }));

      localStorage.setItem('current_user', JSON.stringify(cognitoUser));

      logger.info('User signed in successfully:', { email, userId: cognitoUser.userId, companyName });
      return cognitoUser;

    } catch (error: unknown) {
      logger.error('Cognito signin error:', error);

      const err = error as Error;
      if (error && typeof error === 'object' && 'name' in error) {
        const cognitoError = error as CognitoIdentityProviderServiceException;
        if (cognitoError.name === 'NotAuthorizedException') {
          throw new ValidationError('Invalid email or password');
        }
        if (cognitoError.name === 'UserNotConfirmedException') {
          throw new ValidationError('Please confirm your email address first');
        }
      }

      throw new NetworkError(`Sign-in failed: ${err.message}`);
    }
  }

  async completeNewPasswordChallenge(email: string, newPassword: string, session: string): Promise<CognitoUser> {
    if (!email || !newPassword || !session) {
      throw new ValidationError('Email, new password, and session are required');
    }

    if (newPassword.length < 8) {
      throw new ValidationError('Password must be at least 8 characters long');
    }

    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: this.clientId,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
          USERNAME: email,
          NEW_PASSWORD: newPassword
        }
      });

      const response = await this.client.send(command);

      if (!response.AuthenticationResult) {
        throw new NetworkError('Password change failed - no tokens received');
      }

      // Get user details
      const getUserCommand = new GetUserCommand({
        AccessToken: response.AuthenticationResult.AccessToken!
      });

      const userResponse = await this.client.send(getUserCommand);

      const name = userResponse.UserAttributes?.find(attr => attr.Name === 'name')?.Value || '';
      const companyName = userResponse.UserAttributes?.find(attr => attr.Name === 'custom:companyName')?.Value || '';

      const cognitoUser: CognitoUser = {
        userId: userResponse.Username || email,
        email: email,
        name: name,
        companyName: companyName,
        accessToken: response.AuthenticationResult.AccessToken!,
        idToken: response.AuthenticationResult.IdToken!,
        refreshToken: response.AuthenticationResult.RefreshToken!
      };

      // Store tokens
      localStorage.setItem('cognito_tokens', JSON.stringify({
        accessToken: cognitoUser.accessToken,
        idToken: cognitoUser.idToken,
        refreshToken: cognitoUser.refreshToken
      }));

      localStorage.setItem('current_user', JSON.stringify(cognitoUser));

      logger.info('Password changed and user signed in successfully:', { email, userId: cognitoUser.userId });
      return cognitoUser;

    } catch (error: unknown) {
      logger.error('Complete new password error:', error);

      const err = error as Error;
      if (error && typeof error === 'object' && 'name' in error) {
        const cognitoError = error as CognitoIdentityProviderServiceException;
        if (cognitoError.name === 'InvalidPasswordException') {
          throw new ValidationError('Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, number, and special character');
        }
      }

      throw new NetworkError(`Password change failed: ${err.message}`);
    }
  }

  async signOut(): Promise<void> {
    try {
      // Clear local storage
      localStorage.removeItem('cognito_tokens');
      localStorage.removeItem('current_user');
      
      logger.info('User signed out successfully');
    } catch (error) {
      logger.error('Sign out error:', error);
    }
  }

  getCurrentUser(): CognitoUser | null {
    try {
      const userJson = localStorage.getItem('current_user');
      if (!userJson) return null;

      const user = JSON.parse(userJson) as CognitoUser;

      // Basic validation that we have required fields
      // Note: companyName is optional and may be empty string
      if (!user.userId || !user.email || !user.accessToken) {
        logger.warn('Invalid user data in localStorage, clearing', {
          hasUserId: !!user.userId,
          hasEmail: !!user.email,
          hasAccessToken: !!user.accessToken
        });
        this.signOut();
        return null;
      }

      return user;
    } catch (error) {
      logger.error('Error getting current user:', error);
      this.signOut();
      return null;
    }
  }

  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    return user !== null;
  }

  async refreshTokens(): Promise<void> {
    try {
      const tokensJson = localStorage.getItem('cognito_tokens');
      if (!tokensJson) {
        throw new Error('No refresh token available');
      }

      const tokens = JSON.parse(tokensJson);
      if (!tokens.refreshToken) {
        throw new Error('No refresh token available');
      }

      const command = new InitiateAuthCommand({
        ClientId: this.clientId,
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: {
          REFRESH_TOKEN: tokens.refreshToken
        }
      });

      const response = await this.client.send(command);

      if (!response.AuthenticationResult) {
        throw new Error('Token refresh failed');
      }

      // Update stored tokens
      const newTokens = {
        accessToken: response.AuthenticationResult.AccessToken!,
        idToken: response.AuthenticationResult.IdToken!,
        refreshToken: tokens.refreshToken // Keep existing refresh token
      };

      localStorage.setItem('cognito_tokens', JSON.stringify(newTokens));

      // Update current user with new tokens
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        currentUser.accessToken = newTokens.accessToken;
        currentUser.idToken = newTokens.idToken;
        localStorage.setItem('current_user', JSON.stringify(currentUser));
      }

      logger.info('Tokens refreshed successfully');

    } catch (error) {
      logger.error('Token refresh failed:', error);
      this.signOut();
      throw new NetworkError('Session expired. Please sign in again.');
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!currentPassword || !newPassword) {
      throw new ValidationError('Current password and new password are required');
    }

    if (newPassword.length < 8) {
      throw new ValidationError('New password must be at least 8 characters long');
    }

    if (currentPassword === newPassword) {
      throw new ValidationError('New password must be different from current password');
    }

    try {
      const tokensJson = localStorage.getItem('cognito_tokens');
      if (!tokensJson) {
        throw new NetworkError('No authentication token found. Please sign in again.');
      }

      const tokens = JSON.parse(tokensJson);
      if (!tokens.accessToken) {
        throw new NetworkError('No access token available. Please sign in again.');
      }

      const command = new ChangePasswordCommand({
        AccessToken: tokens.accessToken,
        PreviousPassword: currentPassword,
        ProposedPassword: newPassword
      });

      await this.client.send(command);
      logger.info('Password changed successfully');

    } catch (error: unknown) {
      logger.error('Change password error:', error);

      const err = error as Error;
      if (error && typeof error === 'object' && 'name' in error) {
        const cognitoError = error as CognitoIdentityProviderServiceException;
        if (cognitoError.name === 'NotAuthorizedException') {
          throw new ValidationError('Current password is incorrect');
        }
        if (cognitoError.name === 'InvalidPasswordException') {
          throw new ValidationError('New password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, number, and special character');
        }
        if (cognitoError.name === 'LimitExceededException') {
          throw new ValidationError('Too many password change attempts. Please try again later');
        }
      }

      if (error instanceof ValidationError || error instanceof NetworkError) {
        throw error;
      }

      throw new NetworkError(`Password change failed: ${err.message}`);
    }
  }
}

export const cognitoAuth = new CognitoAuthService();